import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Home, Settings, BarChart3, Zap, FileText, Users, ChevronLeft, ChevronRight, ChevronDown, Activity, TrendingUp } from 'lucide-react';
import { useTheme } from '../../contexts/theme-context';
import { cn } from '../../lib/utils';

const Sidebar = ({
  onCollapseChange,
  isOpen = false,
  onOpenChange
}) => {
  const {
    theme
  } = useTheme();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});

  const handleCollapseToggle = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    onCollapseChange?.(newCollapsedState);
  };

  const toggleExpanded = (name) => {
    setExpandedItems(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };
  const navigation = [{
    name: 'Home',
    href: '/home',
    icon: Home
  }, {
    name: 'Technical Parameters',
    href: '/plant',
    icon: Settings,
    subItems: [
      { name: 'Technical Parameters', href: '/plant/techpar' },
      { name: 'Performance Parameters', href: '/plant/performpar' },
      { name: 'Historical Tech Parameters', href: '/plant/histtechpar' },
      { name: 'Daily Summary', href: '/plant/summary' },
    ]
  }, {
    name: 'Energy Loss',
    href: '/energy',
    icon: BarChart3
  }, {
    name: 'Plant Availability',
    href: '/plant-availability',
    icon: Activity,
    subItems: [
      { name: 'Inverter', href: '/plant-availability/inverter' },
      { name: 'String Combiner Box', href: '/plant-availability/string-combiner-box' },
      { name: 'Tracker', href: '/plant-availability/tracker' }
    ]
  }, {
    name: 'Power Forecast',
    href: '/power',
    icon: Zap,
    subItems: [
      { name: 'Intra Day', href: '/power/intra' },
      { name: 'Inter Day', href: '/power/inter' },
      { name: 'Week Ahead', href: '/power/week' },
      { name: 'Month Ahead', href: '/power/month' },
      { name: 'Historical Data', href: '/power/historical-data' }
    ]
  }, {
    name: 'Asset Management',
    href: '/assetpage',
    icon: Users,
    subItems: [
      { name: 'Inverter', href: '/assetpage/inverter' },
      { name: 'Weather Sensor', href: '/assetpage/weathersensor' }
    ]
  }, {
    name: 'Reports',
    href: '/report',
    icon: FileText
  }];
  return <>
      {/* Sidebar */}
      <div className={`fixed left-0 top-0 h-full bg-white dark:bg-gray-800 shadow-lg z-50 transition-all duration-300 ease-in-out 
        ${isCollapsed ? 'w-16' : 'w-64'} 
        ${theme === 'dark' ? 'border-r border-gray-700' : 'border-r border-gray-200'}
        ${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-700">
          {!isCollapsed && <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-orange-500 via-red-500 to-blue-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xs">RP</span>
              </div>
              <span className="text-xl font-bold text-gray-900 dark:text-white">
                REXON POWER
              </span>
            </div>}
          <button onClick={handleCollapseToggle} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition-colors">
            {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="mt-6 px-2">
          <ul className="space-y-2">
            {navigation.map(item => {
              const Icon = item.icon;
              const hasSubItems = item.subItems && item.subItems.length > 0;
              const isActive = location.pathname.startsWith(item.href);
              const isExpanded = expandedItems[item.name] || isActive;

              return (
                <li key={item.name}>
                  {hasSubItems ? (
                    <>
                      <button
                        onClick={() => toggleExpanded(item.name)}
                        className={cn(
                          "w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200",
                          isActive
                            ? "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-200 shadow-sm"
                            : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white"
                        )}
                        title={isCollapsed ? item.name : undefined}
                      >
                        <div className="flex items-center space-x-3">
                          <Icon className="h-5 w-5 flex-shrink-0" />
                          {!isCollapsed && <span className="truncate">{item.name}</span>}
                        </div>
                        {!isCollapsed && (
                          <ChevronDown
                            className={cn(
                              "h-4 w-4 transition-transform duration-200",
                              isExpanded ? "rotate-180" : ""
                            )}
                          />
                        )}
                      </button>
                      {!isCollapsed && isExpanded && (
                        <ul className="mt-2 ml-8 space-y-1">
                          {item.subItems.map(subItem => (
                            <li key={subItem.href}>
                              <NavLink
                                to={subItem.href}
                                className={({ isActive }) =>
                                  cn(
                                    "block px-3 py-2 rounded-lg text-sm transition-colors duration-200",
                                    isActive
                                      ? "bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white"
                                      : "text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-700/50"
                                  )
                                }
                                onClick={() => onOpenChange?.(false)}
                              >
                                {subItem.name}
                              </NavLink>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  ) : (
                    <NavLink
                      to={item.href}
                      className={({ isActive }) =>
                        cn(
                          "flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200",
                          isActive
                            ? "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-200 shadow-sm"
                            : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white"
                        )
                      }
                      onClick={() => onOpenChange?.(false)}
                      title={isCollapsed ? item.name : undefined}
                    >
                      <Icon className="h-5 w-5 flex-shrink-0" />
                      {!isCollapsed && <span className="truncate">{item.name}</span>}
                    </NavLink>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Bottom Section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse flex-shrink-0"></div>
            {!isCollapsed && <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                System Online
              </span>}
          </div>
        </div>
      </div>

      {/* Overlay for mobile */}
      <div className={`fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden ${isOpen ? 'block' : 'hidden'}`} onClick={() => onOpenChange?.(false)} />
    </>;
};
export default Sidebar;